import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import AppHeader from '../components/ui/AppHeader';
import { ArrowLeft, BarChart3, TrendingUp, Filter, Video, Activity, Instagram, Facebook, RefreshCw, ExternalLink, Heart, MessageCircle, Eye, Users, ThumbsUp, ThumbsDown, MousePointerClick, Info } from 'lucide-react';
import './MetaInsightsPage.css';

// Types
interface MetaDayData {
    date: string;
    campaigns: any[];
    adsets: any[];
    ads: any[];
    totalSpend: number;
    totalImpressions: number;
    totalClicks: number;
    totalReach: number;
}

interface PageDayData {
    date: string;
    // FB Page
    page_views_total?: number;
    page_fans?: number;
    page_fan_adds?: number;
    page_fan_removes?: number;
    page_impressions?: number;
    page_impressions_organic?: number;
    page_impressions_paid?: number;
    page_engaged_users?: number;
    page_post_engagements?: number;
    page_negative_feedback?: number;
    page_actions_post_reactions_total?: any;
    page_consumptions?: number;
    page_total_actions?: number;
    // IG
    ig_impressions?: number;
    ig_reach?: number;
    ig_profile_views?: number;
    ig_website_clicks?: number;
    ig_follower_count?: number;
}

interface IGPost {
    id: string;
    caption: string;
    mediaType: string;
    mediaUrl: string;
    thumbnailUrl: string;
    timestamp: string;
    likes: number;
    comments: number;
    permalink: string;
}

type TimeRange = '7d' | '30d' | '90d' | '1y' | 'all';

const fmt = (n: number) => n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n.toString();
const fmtCur = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

const rankClass = (r: string) => {
    if (!r || r === '') return 'average';
    const l = r.toLowerCase();
    if (l.includes('above') || l === 'above_average_35th_to_55th_pctl' || l === 'above_average_55th_pctl') return 'above';
    if (l.includes('below') || l === 'below_average_10th_pctl' || l === 'below_average_20th_pctl' || l === 'below_average_35th_pctl') return 'below';
    return 'average';
};

const rankLabel = (r: string) => {
    if (!r || r === '') return '—';
    const l = r.toLowerCase();
    if (l.includes('above')) return 'Above Avg';
    if (l.includes('below')) return 'Below Avg';
    return 'Average';
};

export const MetaInsightsPage: React.FC = () => {
    const navigate = useNavigate();
    const [timeRange, setTimeRange] = useState<TimeRange>('30d');
    const [metaDayData, setMetaDayData] = useState<MetaDayData[]>([]);
    const [pageDayData, setPageDayData] = useState<PageDayData[]>([]);
    const [igPosts, setIGPosts] = useState<IGPost[]>([]);
    const [syncing, setSyncing] = useState(false);
    const [tooltip, setTooltip] = useState<string | null>(null);

    // Firestore listeners
    useEffect(() => {
        const q1 = query(collection(db, 'metaAdInsights'));
        const unsub1 = onSnapshot(q1, (snap) => {
            const days: MetaDayData[] = [];
            snap.forEach((d) => {
                if (d.id === '_meta') return;
                const data = d.data();
                days.push({
                    date: d.id,
                    campaigns: data.campaigns || [],
                    adsets: data.adsets || [],
                    ads: data.ads || [],
                    totalSpend: data.totalSpend || 0,
                    totalImpressions: data.totalImpressions || 0,
                    totalClicks: data.totalClicks || 0,
                    totalReach: data.totalReach || 0,
                });
            });
            days.sort((a, b) => a.date.localeCompare(b.date));
            setMetaDayData(days);
        });

        const q2 = query(collection(db, 'metaPageInsights'));
        const unsub2 = onSnapshot(q2, (snap) => {
            const days: PageDayData[] = [];
            snap.forEach((d) => {
                days.push({ date: d.id, ...d.data() } as PageDayData);
            });
            days.sort((a, b) => a.date.localeCompare(b.date));
            setPageDayData(days);
        });

        const q3 = query(collection(db, 'metaIGMedia'));
        const unsub3 = onSnapshot(q3, (snap) => {
            const posts: IGPost[] = [];
            snap.forEach((d) => {
                const data = d.data();
                posts.push({ id: d.id, ...data } as IGPost);
            });
            posts.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
            setIGPosts(posts);
        });

        return () => { unsub1(); unsub2(); unsub3(); };
    }, []);

    // Filter data by time range
    const filteredData = useMemo(() => {
        const now = new Date();
        if (timeRange === 'all') {
            return { adDays: metaDayData, pageDays: pageDayData, daysCount: metaDayData.length };
        }
        const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365;
        const startDate = new Date(now.getTime() - days * 86400000).toISOString().split('T')[0];

        const adDays = metaDayData.filter(d => d.date >= startDate);
        const pageDays = pageDayData.filter(d => d.date >= startDate);

        return { adDays, pageDays, daysCount: days };
    }, [metaDayData, pageDayData, timeRange]);

    // ─── Ad Quality Diagnostics ─────────────────────────
    const qualityData = useMemo(() => {
        const adMap = new Map<string, any>();
        for (const day of filteredData.adDays) {
            for (const ad of day.ads) {
                if (!ad.qualityRanking && !ad.engagementRateRanking && !ad.conversionRateRanking) continue;
                const ex = adMap.get(ad.adId) || {
                    name: ad.adName, campaignName: day.campaigns?.find((c: any) => c.campaignId === ad.campaignId)?.campaignName || '',
                    spend: 0, impressions: 0, clicks: 0,
                    qualityRanking: ad.qualityRanking || '',
                    engagementRateRanking: ad.engagementRateRanking || '',
                    conversionRateRanking: ad.conversionRateRanking || '',
                };
                ex.spend += ad.spend;
                ex.impressions += ad.impressions;
                ex.clicks += ad.clicks;
                // Keep latest ranking
                if (ad.qualityRanking) ex.qualityRanking = ad.qualityRanking;
                if (ad.engagementRateRanking) ex.engagementRateRanking = ad.engagementRateRanking;
                if (ad.conversionRateRanking) ex.conversionRateRanking = ad.conversionRateRanking;
                adMap.set(ad.adId, ex);
            }
        }
        const ads = Array.from(adMap.values()).sort((a, b) => b.spend - a.spend);
        const belowCount = ads.filter(a =>
            rankClass(a.qualityRanking) === 'below' ||
            rankClass(a.engagementRateRanking) === 'below' ||
            rankClass(a.conversionRateRanking) === 'below'
        ).length;
        const aboveCount = ads.filter(a =>
            rankClass(a.qualityRanking) === 'above' &&
            rankClass(a.engagementRateRanking) === 'above'
        ).length;
        return { ads, belowCount, aboveCount, total: ads.length };
    }, [filteredData.adDays]);

    // ─── Video Performance ──────────────────────────────
    const videoData = useMemo(() => {
        let plays = 0, p25 = 0, p50 = 0, p75 = 0, p100 = 0, avgWatchTime = 0, videoAds = 0, totalSpend = 0;
        for (const day of filteredData.adDays) {
            for (const ad of day.ads) {
                if ((ad.videoPlays || 0) > 0) {
                    plays += ad.videoPlays || 0;
                    p25 += ad.videoP25 || 0;
                    p50 += ad.videoP50 || 0;
                    p75 += ad.videoP75 || 0;
                    p100 += ad.videoP100 || 0;
                    avgWatchTime += (ad.videoAvgWatchTime || 0) * (ad.videoPlays || 0);
                    totalSpend += ad.spend;
                    videoAds++;
                }
            }
        }
        const weightedAvgWatch = plays > 0 ? avgWatchTime / plays : 0;
        return {
            plays, p25, p50, p75, p100,
            avgWatchTime: weightedAvgWatch,
            costPerPlay: plays > 0 ? totalSpend / plays : 0,
            costPerThruPlay: p100 > 0 ? totalSpend / p100 : 0,
            retentionP25: plays > 0 ? (p25 / plays) * 100 : 0,
            retentionP50: plays > 0 ? (p50 / plays) * 100 : 0,
            retentionP75: plays > 0 ? (p75 / plays) * 100 : 0,
            retentionP100: plays > 0 ? (p100 / plays) * 100 : 0,
            hasVideoData: plays > 0,
            videoAds,
        };
    }, [filteredData.adDays]);

    // ─── Funnel Leak Analysis ──────────────────────────
    const funnelData = useMemo(() => {
        let impressions = 0, allClicks = 0, outboundClicks = 0, linkClicks = 0, lpv = 0, leads = 0;
        for (const day of filteredData.adDays) {
            impressions += day.totalImpressions;
            allClicks += day.totalClicks;
            for (const ad of day.ads) {
                outboundClicks += ad.outboundClicks || 0;
                linkClicks += ad.linkClicks || 0;
                lpv += ad.landingPageViews || 0;
                leads += ad.leads || 0;
            }
        }
        return {
            impressions, allClicks, outboundClicks, linkClicks, lpv, leads,
            clickToOutbound: allClicks > 0 ? (outboundClicks / allClicks) * 100 : 0,
            outboundToLPV: outboundClicks > 0 ? (lpv / outboundClicks) * 100 : 0,
            lpvToLead: lpv > 0 ? (leads / lpv) * 100 : 0,
            overallConversion: impressions > 0 ? (leads / impressions) * 100 : 0,
        };
    }, [filteredData.adDays]);

    // ─── IG Organic ─────────────────────────────────────
    const igData = useMemo(() => {
        const days = filteredData.pageDays.filter(d => d.ig_impressions !== undefined);
        if (days.length === 0) return null;

        const totalImpressions = days.reduce((s, d) => s + (d.ig_impressions || 0), 0);
        const totalReach = days.reduce((s, d) => s + (d.ig_reach || 0), 0);
        const totalProfileViews = days.reduce((s, d) => s + (d.ig_profile_views || 0), 0);
        const totalWebClicks = days.reduce((s, d) => s + (d.ig_website_clicks || 0), 0);

        // Follower trend
        const followerDays = days.filter(d => d.ig_follower_count !== undefined && d.ig_follower_count! > 0);
        const latestFollowers = followerDays.length > 0 ? followerDays[followerDays.length - 1].ig_follower_count || 0 : 0;
        const earliestFollowers = followerDays.length > 0 ? followerDays[0].ig_follower_count || 0 : 0;
        const followerGrowth = latestFollowers - earliestFollowers;

        return {
            totalImpressions, totalReach, totalProfileViews, totalWebClicks,
            latestFollowers, followerGrowth, daysCount: days.length,
            avgDailyReach: days.length > 0 ? totalReach / days.length : 0,
            avgDailyProfileViews: days.length > 0 ? totalProfileViews / days.length : 0,
        };
    }, [filteredData.pageDays]);

    // ─── FB Page Health ─────────────────────────────────
    const pageHealthData = useMemo(() => {
        const days = filteredData.pageDays.filter(d => d.page_views_total !== undefined);
        if (days.length === 0) return null;

        const totalViews = days.reduce((s, d) => s + (d.page_views_total || 0), 0);
        const totalEngagedUsers = days.reduce((s, d) => s + (d.page_engaged_users || 0), 0);
        const totalPostEngagements = days.reduce((s, d) => s + (d.page_post_engagements || 0), 0);
        const totalImpressions = days.reduce((s, d) => s + (d.page_impressions || 0), 0);
        const organicImpressions = days.reduce((s, d) => s + (d.page_impressions_organic || 0), 0);
        const paidImpressions = days.reduce((s, d) => s + (d.page_impressions_paid || 0), 0);
        const negativeFeedback = days.reduce((s, d) => s + (d.page_negative_feedback || 0), 0);
        const fanAdds = days.reduce((s, d) => s + (d.page_fan_adds || 0), 0);
        const fanRemoves = days.reduce((s, d) => s + (d.page_fan_removes || 0), 0);
        const latestFans = days[days.length - 1]?.page_fans || 0;
        const totalConsumptions = days.reduce((s, d) => s + (d.page_consumptions || 0), 0);

        return {
            totalViews, totalEngagedUsers, totalPostEngagements, totalImpressions,
            organicImpressions, paidImpressions, negativeFeedback,
            fanAdds, fanRemoves, latestFans, totalConsumptions,
            organicPct: totalImpressions > 0 ? (organicImpressions / totalImpressions) * 100 : 0,
            netFanGrowth: fanAdds - fanRemoves,
            daysCount: days.length,
        };
    }, [filteredData.pageDays]);

    // Sync functions
    const handleSync = async () => {
        setSyncing(true);
        try {
            await Promise.all([
                fetch('https://us-central1-lctf-projects.cloudfunctions.net/syncMetaAdInsights?force=true'),
                fetch('https://us-central1-lctf-projects.cloudfunctions.net/syncMetaPageInsights'),
            ]);
        } catch (e) {
            console.error('Sync error:', e);
        }
        setSyncing(false);
    };

    const hasAnyData = metaDayData.length > 0 || pageDayData.length > 0;

    return (
        <div className="meta-insights-page">
            <AppHeader />
            <div className="meta-insights-content">
                {/* Header */}
                <div className="meta-page-header">
                    <div className="meta-page-header-left">
                        <button className="meta-back-btn" onClick={() => navigate('/analytics')}>
                            <ArrowLeft size={16} /> Analytics
                        </button>
                        <div className="meta-page-title-block">
                            <div className="meta-page-icon"><Facebook size={22} /></div>
                            <div>
                                <h1>Meta Insights</h1>
                                <p>Ad quality · Video performance · Funnel analysis · IG & FB organic</p>
                            </div>
                        </div>
                    </div>
                    <div className="meta-page-header-right">
                        <div className="meta-time-tabs">
                            {(['7d', '30d', '90d', '1y', 'all'] as TimeRange[]).map(range => (
                                <button
                                    key={range}
                                    className={`meta-time-tab ${timeRange === range ? 'active' : ''}`}
                                    onClick={() => setTimeRange(range)}
                                >
                                    {range === 'all' ? 'All Time' : range}
                                </button>
                            ))}
                        </div>
                        <button className="meta-sync-btn" onClick={handleSync} disabled={syncing}>
                            <RefreshCw size={14} className={syncing ? 'spinning' : ''} />
                            {syncing ? 'Syncing...' : 'Sync Now'}
                        </button>
                    </div>
                </div>

                {!hasAnyData ? (
                    <div className="meta-section">
                        <div className="meta-empty-state">
                            <BarChart3 size={40} />
                            <p>No Meta data synced yet. Click "Sync Now" to pull data from your Meta accounts.</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ━━━ 1. Ad Quality Diagnostics ━━━ */}
                        {qualityData.total > 0 && (
                            <div className="meta-section">
                                <div className="meta-section-header">
                                    <div className="meta-section-icon blue"><BarChart3 size={18} /></div>
                                    <div>
                                        <h2>Ad Quality Diagnostics</h2>
                                        <p>Meta's quality, engagement, and conversion rankings vs competitors in the same auction</p>
                                    </div>
                                </div>

                                <div className="meta-metric-grid">
                                    <div className="meta-metric-card">
                                        <div className="meta-metric-label">Ads with Rankings</div>
                                        <div className="meta-metric-value">{qualityData.total}</div>
                                    </div>
                                    <div className="meta-metric-card">
                                        <div className="meta-metric-label">Above Average</div>
                                        <div className="meta-metric-value good">{qualityData.aboveCount}</div>
                                        <div className="meta-metric-detail">{qualityData.total > 0 ? ((qualityData.aboveCount / qualityData.total) * 100).toFixed(0) : 0}% of ads</div>
                                    </div>
                                    <div className="meta-metric-card">
                                        <div className="meta-metric-label">Below Average</div>
                                        <div className={`meta-metric-value ${qualityData.belowCount > 0 ? 'bad' : 'good'}`}>{qualityData.belowCount}</div>
                                        <div className="meta-metric-detail">{qualityData.belowCount > 0 ? 'Action needed — refresh creative or adjust targeting' : 'All ads performing well'}</div>
                                    </div>
                                </div>

                                <div className="meta-quality-table">
                                    <div className="meta-quality-header">
                                        <span>Ad Name</span>
                                        <span>Quality</span>
                                        <span>Engagement</span>
                                        <span>Conversion</span>
                                        <span>Spend</span>
                                        <span>CTR</span>
                                    </div>
                                    {qualityData.ads.slice(0, 15).map((ad, i) => (
                                        <div key={i} className="meta-quality-row">
                                            <span className="meta-quality-name" title={ad.name}>{ad.name}</span>
                                            <span><span className={`meta-ranking-badge ${rankClass(ad.qualityRanking)}`}>{rankLabel(ad.qualityRanking)}</span></span>
                                            <span><span className={`meta-ranking-badge ${rankClass(ad.engagementRateRanking)}`}>{rankLabel(ad.engagementRateRanking)}</span></span>
                                            <span><span className={`meta-ranking-badge ${rankClass(ad.conversionRateRanking)}`}>{rankLabel(ad.conversionRateRanking)}</span></span>
                                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtCur(ad.spend)}</span>
                                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0.00'}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ━━━ 2. Video Performance ━━━ */}
                        {videoData.hasVideoData && (
                            <div className="meta-section">
                                <div className="meta-section-header">
                                    <div className="meta-section-icon purple"><Video size={18} /></div>
                                    <div>
                                        <h2>Video Performance</h2>
                                        <p>ThruPlay completions, audience retention, and watch time across {videoData.videoAds} video ads</p>
                                    </div>
                                </div>

                                <div className="meta-metric-grid">
                                    <div className="meta-metric-card">
                                        <div className="meta-metric-label">Video Plays</div>
                                        <div className="meta-metric-value">{fmt(videoData.plays)}</div>
                                        <div className="meta-metric-detail">{fmtCur(videoData.costPerPlay)} cost/play</div>
                                    </div>
                                    <div className="meta-metric-card">
                                        <div className="meta-metric-label">ThruPlays (100%)</div>
                                        <div className="meta-metric-value">{fmt(videoData.p100)}</div>
                                        <div className="meta-metric-detail">{videoData.retentionP100.toFixed(1)}% completion rate</div>
                                    </div>
                                    <div className="meta-metric-card">
                                        <div className="meta-metric-label">Cost per ThruPlay</div>
                                        <div className={`meta-metric-value ${videoData.costPerThruPlay > 0.15 ? 'warn' : 'good'}`}>${videoData.costPerThruPlay.toFixed(3)}</div>
                                        <div className="meta-metric-detail">{videoData.costPerThruPlay <= 0.15 ? 'Efficient' : 'Above average'}</div>
                                    </div>
                                    <div className="meta-metric-card">
                                        <div className="meta-metric-label">Avg Watch Time</div>
                                        <div className="meta-metric-value">{videoData.avgWatchTime.toFixed(1)}s</div>
                                    </div>
                                </div>

                                <h4 style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', margin: '0 0 4px' }}>Audience Retention</h4>
                                <p style={{ fontSize: '0.7rem', color: '#94a3b8', margin: '0 0 8px' }}>What % of viewers are still watching at each checkpoint</p>
                                <div className="meta-retention-bars">
                                    {[
                                        { label: 'Start', pct: 100, value: videoData.plays, color: '#3b82f6' },
                                        { label: '25%', pct: videoData.retentionP25, value: videoData.p25, color: '#60a5fa' },
                                        { label: '50%', pct: videoData.retentionP50, value: videoData.p50, color: '#93c5fd' },
                                        { label: '75%', pct: videoData.retentionP75, value: videoData.p75, color: '#bfdbfe' },
                                        { label: '100%', pct: videoData.retentionP100, value: videoData.p100, color: '#16a34a' },
                                    ].map(bar => (
                                        <div key={bar.label} className="meta-retention-bar-col">
                                            <div className="meta-retention-value">{bar.pct.toFixed(0)}%</div>
                                            <div className="meta-retention-bar-wrapper">
                                                <div className="meta-retention-bar" style={{ height: `${bar.pct}%`, background: bar.color }} />
                                            </div>
                                            <div className="meta-retention-label">{bar.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ━━━ 3. Funnel Leak Analysis ━━━ */}
                        <div className="meta-section">
                            <div className="meta-section-header">
                                <div className="meta-section-icon amber"><Filter size={18} /></div>
                                <div>
                                    <h2>Funnel Leak Analysis</h2>
                                    <p>Where you're losing people between impression and lead — find and fix the leaks</p>
                                </div>
                            </div>

                            <div className="meta-funnel">
                                {[
                                    { label: 'Impressions', value: funnelData.impressions, pct: 100 },
                                    { label: 'All Clicks', value: funnelData.allClicks, pct: funnelData.impressions > 0 ? (funnelData.allClicks / funnelData.impressions) * 100 : 0 },
                                    { label: 'Outbound Clicks', value: funnelData.outboundClicks, pct: funnelData.impressions > 0 ? (funnelData.outboundClicks / funnelData.impressions) * 100 : 0 },
                                    { label: 'Landing Page Views', value: funnelData.lpv, pct: funnelData.impressions > 0 ? (funnelData.lpv / funnelData.impressions) * 100 : 0 },
                                    { label: 'Leads', value: funnelData.leads, pct: funnelData.impressions > 0 ? (funnelData.leads / funnelData.impressions) * 100 : 0 },
                                ].map((step, i, arr) => {
                                    const dropPct = i > 0 && arr[i - 1].value > 0
                                        ? ((arr[i - 1].value - step.value) / arr[i - 1].value) * 100
                                        : 0;
                                    const dropClass = dropPct > 60 ? 'bad' : dropPct > 30 ? 'warn' : 'ok';
                                    return (
                                        <div key={step.label} className="meta-funnel-step">
                                            <div className="funnel-value">{fmt(step.value)}</div>
                                            <div className="funnel-label">{step.label}</div>
                                            {i > 0 && step.value > 0 && (
                                                <div className={`funnel-drop ${dropClass}`}>-{dropPct.toFixed(0)}% drop</div>
                                            )}
                                            <div className="meta-funnel-bar">
                                                <div className="meta-funnel-bar-fill" style={{
                                                    width: `${step.pct > 100 ? 100 : step.pct}%`,
                                                    background: i === arr.length - 1 ? '#16a34a' : '#3b82f6'
                                                }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="meta-metric-grid" style={{ marginTop: 8 }}>
                                <div className="meta-metric-card">
                                    <div className="meta-metric-label">
                                        Real Click Rate
                                        <span className="fb-tooltip-trigger" style={{ marginLeft: 4 }} onMouseEnter={() => setTooltip('outbound')} onMouseLeave={() => setTooltip(null)}>
                                            <Info size={10} />
                                            {tooltip === 'outbound' && <span className="fb-tooltip-popup">% of clicks that actually left Facebook. The rest are profile clicks, "see more", etc.</span>}
                                        </span>
                                    </div>
                                    <div className="meta-metric-value">{funnelData.clickToOutbound.toFixed(1)}%</div>
                                    <div className="meta-metric-detail">of clicks are outbound</div>
                                </div>
                                <div className="meta-metric-card">
                                    <div className="meta-metric-label">
                                        Landing Page Load Rate
                                        <span className="fb-tooltip-trigger" style={{ marginLeft: 4 }} onMouseEnter={() => setTooltip('lpvrate')} onMouseLeave={() => setTooltip(null)}>
                                            <Info size={10} />
                                            {tooltip === 'lpvrate' && <span className="fb-tooltip-popup">Low rate = slow landing page or tracking issues. Should be 70%+.</span>}
                                        </span>
                                    </div>
                                    <div className={`meta-metric-value ${funnelData.outboundToLPV >= 70 ? 'good' : funnelData.outboundToLPV >= 50 ? 'warn' : 'bad'}`}>
                                        {funnelData.outboundToLPV.toFixed(1)}%
                                    </div>
                                    <div className="meta-metric-detail">outbound click → page loaded</div>
                                </div>
                                <div className="meta-metric-card">
                                    <div className="meta-metric-label">Lead Conversion Rate</div>
                                    <div className={`meta-metric-value ${funnelData.lpvToLead >= 10 ? 'good' : funnelData.lpvToLead >= 5 ? 'warn' : ''}`}>
                                        {funnelData.lpvToLead.toFixed(1)}%
                                    </div>
                                    <div className="meta-metric-detail">landing page → lead</div>
                                </div>
                                <div className="meta-metric-card">
                                    <div className="meta-metric-label">Overall Conversion</div>
                                    <div className="meta-metric-value">{funnelData.overallConversion.toFixed(3)}%</div>
                                    <div className="meta-metric-detail">impression → lead</div>
                                </div>
                            </div>
                        </div>

                        {/* ━━━ 4 & 5: IG Organic + FB Page (two-col) ━━━ */}
                        <div className="meta-two-col">
                            {/* IG Organic Growth */}
                            <div className="meta-section">
                                <div className="meta-section-header">
                                    <div className="meta-section-icon rose"><Instagram size={18} /></div>
                                    <div>
                                        <h2>Instagram Organic</h2>
                                        <p>{igData ? `${igData.daysCount} days of organic data` : 'Sync to load IG data'}</p>
                                    </div>
                                </div>

                                {igData ? (
                                    <>
                                        <div className="meta-metric-grid">
                                            <div className="meta-metric-card">
                                                <div className="meta-metric-label">Followers</div>
                                                <div className="meta-metric-value">{fmt(igData.latestFollowers)}</div>
                                                <div className={`meta-metric-detail ${igData.followerGrowth >= 0 ? '' : ''}`}>
                                                    {igData.followerGrowth >= 0 ? '+' : ''}{igData.followerGrowth} this period
                                                </div>
                                            </div>
                                            <div className="meta-metric-card">
                                                <div className="meta-metric-label">Reach</div>
                                                <div className="meta-metric-value">{fmt(igData.totalReach)}</div>
                                                <div className="meta-metric-detail">{fmt(Math.round(igData.avgDailyReach))}/day avg</div>
                                            </div>
                                            <div className="meta-metric-card">
                                                <div className="meta-metric-label">Profile Views</div>
                                                <div className="meta-metric-value">{fmt(igData.totalProfileViews)}</div>
                                                <div className="meta-metric-detail">{fmt(Math.round(igData.avgDailyProfileViews))}/day avg</div>
                                            </div>
                                            <div className="meta-metric-card">
                                                <div className="meta-metric-label">Website Clicks</div>
                                                <div className="meta-metric-value">{igData.totalWebClicks}</div>
                                                <div className="meta-metric-detail">from profile/bio link</div>
                                            </div>
                                        </div>

                                        {igPosts.length > 0 && (
                                            <>
                                                <h4 style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', margin: '16px 0 10px' }}>Recent Posts</h4>
                                                <div className="meta-ig-grid">
                                                    {igPosts.slice(0, 8).map(post => (
                                                        <a key={post.id} className="meta-ig-post" href={post.permalink} target="_blank" rel="noopener noreferrer">
                                                            {(post.mediaUrl || post.thumbnailUrl) && (
                                                                <img className="meta-ig-post-img" src={post.thumbnailUrl || post.mediaUrl} alt={post.caption || 'IG post'} loading="lazy" />
                                                            )}
                                                            <div className="meta-ig-post-info">
                                                                <span className="meta-ig-post-type">{post.mediaType}</span>
                                                                <div className="meta-ig-post-stats">
                                                                    <span><Heart size={12} /> {post.likes}</span>
                                                                    <span><MessageCircle size={12} /> {post.comments}</span>
                                                                </div>
                                                                {post.caption && <div className="meta-ig-post-caption">{post.caption}</div>}
                                                            </div>
                                                        </a>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <div className="meta-empty-state">
                                        <Instagram size={32} />
                                        <p>No IG data yet. Click "Sync Now" above.</p>
                                    </div>
                                )}
                            </div>

                            {/* FB Page Health */}
                            <div className="meta-section">
                                <div className="meta-section-header">
                                    <div className="meta-section-icon green"><Facebook size={18} /></div>
                                    <div>
                                        <h2>Facebook Page Health</h2>
                                        <p>{pageHealthData ? `${pageHealthData.daysCount} days · ${fmt(pageHealthData.latestFans)} total fans` : 'Sync to load page data'}</p>
                                    </div>
                                </div>

                                {pageHealthData ? (
                                    <div className="meta-page-grid">
                                        <div className="meta-page-card">
                                            <h4><Users size={14} /> Audience</h4>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Total Fans</span>
                                                <span className="meta-page-stat-value">{fmt(pageHealthData.latestFans)}</span>
                                            </div>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">New Likes</span>
                                                <span className="meta-page-stat-value" style={{ color: '#16a34a' }}>+{pageHealthData.fanAdds}</span>
                                            </div>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Unlikes</span>
                                                <span className="meta-page-stat-value" style={{ color: '#dc2626' }}>-{pageHealthData.fanRemoves}</span>
                                            </div>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Net Growth</span>
                                                <span className="meta-page-stat-value" style={{ color: pageHealthData.netFanGrowth >= 0 ? '#16a34a' : '#dc2626' }}>
                                                    {pageHealthData.netFanGrowth >= 0 ? '+' : ''}{pageHealthData.netFanGrowth}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="meta-page-card">
                                            <h4><Eye size={14} /> Reach & Impressions</h4>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Total Impressions</span>
                                                <span className="meta-page-stat-value">{fmt(pageHealthData.totalImpressions)}</span>
                                            </div>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Organic</span>
                                                <span className="meta-page-stat-value">{fmt(pageHealthData.organicImpressions)} ({pageHealthData.organicPct.toFixed(0)}%)</span>
                                            </div>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Paid</span>
                                                <span className="meta-page-stat-value">{fmt(pageHealthData.paidImpressions)}</span>
                                            </div>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Page Views</span>
                                                <span className="meta-page-stat-value">{fmt(pageHealthData.totalViews)}</span>
                                            </div>
                                        </div>

                                        <div className="meta-page-card">
                                            <h4><ThumbsUp size={14} /> Engagement</h4>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Engaged Users</span>
                                                <span className="meta-page-stat-value">{fmt(pageHealthData.totalEngagedUsers)}</span>
                                            </div>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Post Engagements</span>
                                                <span className="meta-page-stat-value">{fmt(pageHealthData.totalPostEngagements)}</span>
                                            </div>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Content Consumptions</span>
                                                <span className="meta-page-stat-value">{fmt(pageHealthData.totalConsumptions)}</span>
                                            </div>
                                        </div>

                                        <div className="meta-page-card">
                                            <h4><ThumbsDown size={14} /> Negative Feedback</h4>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Total Negative Actions</span>
                                                <span className="meta-page-stat-value" style={{ color: pageHealthData.negativeFeedback > 10 ? '#dc2626' : '#64748b' }}>
                                                    {pageHealthData.negativeFeedback}
                                                </span>
                                            </div>
                                            <div className="meta-page-stat-row">
                                                <span className="meta-page-stat-label">Feedback Rate</span>
                                                <span className="meta-page-stat-value">
                                                    {pageHealthData.totalImpressions > 0 ? ((pageHealthData.negativeFeedback / pageHealthData.totalImpressions) * 100).toFixed(3) : '0'}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="meta-empty-state">
                                        <Facebook size={32} />
                                        <p>No FB Page data yet. Click "Sync Now" above.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
